'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { type FocusEvent, type MouseEvent, useEffect, useRef } from 'react';

import {
  TOP_NAV_DATA_GA,
  TOP_NAV_DATA_TEST,
} from '@/app/components/layouts/top-navigation/tracking';
import type { NavItemType } from '@/app/types/top-navigation';
import { createDataGA } from '@/app/utils/createDataGA';
import type { TopNavigationVariantType } from '@/components/layouts/top-navigation/types';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { css } from '@/stitches.config';

const ItemCssStyled = css({
  transition: 'background-color 0.3s ease, color 0.3s ease',
  borderRadius: '$rounded-1',
  px: '$space-2',
  py: '$space-1',
  outline: 'none',
  display: 'none',
  '@xl': {
    display: 'block',
  },
  '@hover': {
    '&:hover': {
      backgroundColor: '#ebebeb', // tailwind gray-50
      color: '$black',
    },
  },
  variants: {
    variant: {
      filled: {
        color: '#555555', // tailwind gray-700
      },
      transparent: {
        color: '$white',
      },
    },
    isActive: {
      true: {
        backgroundColor: '#ebebeb', // tailwind gray-50
        color: '$black',
      },
      false: {},
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

interface DesktopMenuProps {
  isOpen: boolean;
  activeItem?: NavItemType;
  items: NavItemType[];
  variant?: TopNavigationVariantType;
  onSetActiveItem?: (item?: NavItemType) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export const DesktopMenu = ({
  isOpen = false,
  activeItem,
  items = [],
  variant = 'filled',
  onSetActiveItem,
  onOpen,
  onClose,
}: DesktopMenuProps) => {
  const focusRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const searchParam = useSearchParams();
  const openMenu = (
    e: FocusEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>,
    item: NavItemType
  ) => {
    typeof onOpen === 'function' && onOpen();
    typeof onSetActiveItem === 'function' && onSetActiveItem(item);
    /**
     *  Note: Handle in case of when user click the button 1, and it will have the focus state by default.
     *  Then, user hover button 2 then the menu of button 2 open. After that, user navigate away to other tab then returned back to the page
     *  The open menu should still be the button 2. If we don't handle this, it will open button 1 (clicked button) instead.
     */
    if (e && e.target) {
      const button = e.target as HTMLDivElement;
      button.focus();
      focusRef.current = button;
    }
  };
  const onFocusLink = (item: NavItemType) => {
    typeof onClose === 'function' && onClose();
    typeof onSetActiveItem === 'function' && onSetActiveItem(item);
  };
  const closeMenu = () => {
    typeof onClose === 'function' && onClose();
    // Note: To prevent menu open when exit from the tab and then get back
    focusRef.current?.blur();
  };

  useEffect(() => {
    // Note: To prevent menu open when exit from the tab and then get back
    if (!isOpen && focusRef.current) {
      focusRef.current?.blur();
    }
  }, [isOpen]);

  return items.map(
    (item, index) =>
      !item.isOnlyMobileNav &&
      (item.href ? (
        <Link
          key={`menuitem_${index}`}
          role="menuitem"
          aria-label={item.label}
          test={{
            dataTest: TOP_NAV_DATA_TEST.navigationMenuLink,
          }}
          tracking={{
            dataGA: createDataGA({
              eventName: TOP_NAV_DATA_GA.topNavigation,
              text: item.label,
              url: item.href,
              click: true,
            }),
          }}
          className={ItemCssStyled({
            isActive:
              activeItem?.label === item.label && pathname !== activeItem?.href,
            variant,
          })}
          href={item.href}
          onFocus={() => onFocusLink(item)}
          onMouseEnter={closeMenu}
        >
          <Text variant="text-current">{item.label}</Text>
        </Link>
      ) : (
        <Box
          key={`menuitem_${index}`}
          role="menuitem"
          component="button"
          aria-label={item.label}
          test={{
            dataTest: TOP_NAV_DATA_TEST.navigationMenuLink,
          }}
          tracking={{
            dataGA: createDataGA({
              eventName: TOP_NAV_DATA_GA.topNavigation,
              text: item.label,
              click: true,
            }),
          }}
          cursor="pointer"
          className={ItemCssStyled({
            isActive: activeItem?.label === item.label && isOpen,
            variant,
          })}
          border="none"
          backgroundColor="transparent"
          onFocus={(e) => openMenu(e, item)}
          onMouseEnter={(e) => openMenu(e, item)}
        >
          <Text data-test="menuitem_text" variant="text-current">
            {item.label}
          </Text>
        </Box>
      ))
  );
};
